/** 与 Node/SQLite 后端同步用户资料（跨设备以 API 为准） */

export const mergeUniqueKeys = (primary = [], secondary = []) =>
  [...new Set([...(Array.isArray(primary) ? primary : []), ...(Array.isArray(secondary) ? secondary : [])].map(String))];

export const buildContentKey = (item) => {
  const id = String(item?.id ?? item?._id ?? '');
  if (!id) return '';
  const type = item?.type === 'adopt' ? 'adopt' : 'post';
  return `${type}:${id}`;
};

export const parseContentKey = (key) => {
  const raw = String(key || '');
  const idx = raw.indexOf(':');
  if (idx <= 0) return null;
  return { type: raw.slice(0, idx), id: raw.slice(idx + 1) };
};

export const mapServerUserToProfileFields = (serverUser = {}) => ({
  id: serverUser.id ?? serverUser.userId,
  name: serverUser.name,
  bio: serverUser.bio,
  avatar: serverUser.avatar,
  coverImg: serverUser.coverImg,
  location: serverUser.location,
  city: serverUser.city,
  province: serverUser.province,
  locationSource: serverUser.locationSource,
  locationAgreed: serverUser.locationAgreed,
  uid: serverUser.uid,
  role: serverUser.role,
  isVerified: serverUser.isVerified,
  followingList: serverUser.followingList,
  likedContentKeys: serverUser.likedContentKeys,
  bookmarkedContentKeys: serverUser.bookmarkedContentKeys,
  followers: serverUser.followers,
  profileUpdatedAt: serverUser.profileUpdatedAt
});

export const mergeServerProfileIntoAppProfile = (appProfile, serverUser) => {
  if (!serverUser) return appProfile;
  const mapped = mapServerUserToProfileFields(serverUser);
  const serverTs = Number(mapped.profileUpdatedAt || 0);
  const localTs = Number(appProfile?.profileUpdatedAt || 0);
  const preferServer = serverTs >= localTs;

  const pick = (key) => {
    const serverVal = mapped[key];
    const localVal = appProfile?.[key];
    if (preferServer && serverVal != null && String(serverVal).trim() !== '') return serverVal;
    if (localVal != null && String(localVal).trim() !== '') return localVal;
    return serverVal ?? localVal;
  };

  return {
    ...appProfile,
    ...mapped,
    name: pick('name'),
    bio: pick('bio'),
    avatar: pick('avatar'),
    coverImg: pick('coverImg'),
    location: pick('location'),
    city: pick('city'),
    province: pick('province'),
    locationSource: pick('locationSource'),
    locationAgreed: mapped.locationAgreed ?? appProfile?.locationAgreed,
    uid: pick('uid') || appProfile?.uid,
    followingList: mergeUniqueKeys(mapped.followingList, appProfile?.followingList),
    likedContentKeys: mergeUniqueKeys(mapped.likedContentKeys, appProfile?.likedContentKeys),
    bookmarkedContentKeys: mergeUniqueKeys(
      mapped.bookmarkedContentKeys,
      appProfile?.bookmarkedContentKeys
    ),
    profileUpdatedAt: Math.max(serverTs, localTs)
  };
};

export const fetchProfileFromServerApi = async (apiRequest, phone) => {
  if (!phone) return null;
  const endpoints = [
    `/user/profile?phone=${encodeURIComponent(phone)}`,
    `/users/profile?phone=${encodeURIComponent(phone)}`
  ];
  for (const endpoint of endpoints) {
    const result = await apiRequest(endpoint, 'GET');
    if (result?.success && result.user) return result.user;
  }
  return null;
};

export const syncProfileToServerApi = async (apiRequest, profile, patch = {}) => {
  if (!profile?.phone) return null;
  const payload = {
    phone: profile.phone,
    userId: profile.id,
    name: profile.name,
    bio: profile.bio,
    avatar: profile.avatar,
    coverImg: profile.coverImg,
    location: profile.location,
    city: profile.city,
    province: profile.province,
    locationSource: profile.locationSource,
    locationAgreed: profile.locationAgreed,
    uid: profile.uid,
    followingList: profile.followingList,
    likedContentKeys: profile.likedContentKeys,
    bookmarkedContentKeys: profile.bookmarkedContentKeys,
    profileUpdatedAt: profile.profileUpdatedAt || Date.now(),
    ...patch
  };
  const endpoints = ['/user/profile', '/users/profile', '/admin/update-profile'];
  for (const endpoint of endpoints) {
    const result = await apiRequest(endpoint, 'POST', payload);
    if (result?.success && result.user) return result.user;
  }
  return null;
};

export const applyServerUserToSession = (profile, serverUser, applySessionProfile) => {
  if (!serverUser || !profile) return profile;
  const merged = mergeServerProfileIntoAppProfile(profile, serverUser);
  if (typeof applySessionProfile === 'function') {
    applySessionProfile(profile.phone, merged);
  }
  return merged;
};

export const filterFeedItemsByContentKeys = (items = [], keys = []) => {
  const keySet = new Set((Array.isArray(keys) ? keys : []).map(String));
  return items.filter((item) => keySet.has(buildContentKey(item)));
};

export const mergeFeedItemsByContentKeys = (items = [], keys = []) => {
  const keySet = new Set((Array.isArray(keys) ? keys : []).map(String));
  const map = new Map(items.map((item) => [buildContentKey(item), item]));
  keys.forEach((key) => {
    if (!keySet.has(String(key))) return;
    if (map.has(String(key))) return;
    const parsed = parseContentKey(key);
    if (!parsed?.id) return;
    map.set(String(key), { id: parsed.id, type: parsed.type, missing: true });
  });
  return [...map.values()];
};
